// src/pages/UserMigration.jsx
import React, { useState, useRef } from 'react';
import {
    Container, Card, Form, Button, Table, Badge, Spinner, Alert
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

const VALID_ROLES  = ['user', 'agent', 'authorizer'];
const ROLE_LABELS  = { user: 'Usuario', agent: 'Agente', authorizer: 'Autorizador' };
const ROLE_COLORS  = { user: 'secondary', agent: 'primary', authorizer: 'info' };

// ── Parseo de CSV en el navegador ─────────────────────────────────────────────
function parseCSV(text) {
    // Soporta \n, \r\n y \r (Excel Mac antiguo)
    const lines = text.split(/\r?\n|\r/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];

    // Auto-detectar delimitador: punto y coma (Excel español) o coma
    const delimiter = lines[0].includes(';') ? ';' : ',';

    // Detectar si la primera fila es cabecera buscando la palabra "email"
    const firstCols  = lines[0].toLowerCase().split(delimiter).map(c => c.trim());
    const emailIdx   = firstCols.findIndex(c => c === 'email' || c === 'correo');
    const roleIdx    = firstCols.findIndex(c => c === 'role' || c === 'rol');
    const hasHeaders = emailIdx !== -1;

    const dataLines = hasHeaders ? lines.slice(1) : lines;
    const eIdx = hasHeaders ? emailIdx : 0;
    const rIdx = hasHeaders ? roleIdx  : 1;

    return dataLines
        .map(line => {
            const parts   = line.split(delimiter).map(c => c.trim());
            const email   = parts[eIdx] || '';
            const rawRole = rIdx >= 0 && parts[rIdx] ? parts[rIdx].toLowerCase() : '';
            const role    = VALID_ROLES.includes(rawRole) ? rawRole : 'user';
            return { email, role };
        })
        .filter(r => r.email);
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function UserMigration() {
    const [rows,    setRows]    = useState([]);   // filas en preview
    const [results, setResults] = useState(null); // resultados del envío
    const [sending, setSending] = useState(false);
    const fileRef = useRef();

    // Cargar y parsear CSV
    const handleFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const parsed = parseCSV(ev.target.result);
            if (!parsed.length) {
                toast.warn('No se encontraron filas válidas en el CSV');
                return;
            }
            setRows(parsed.map((r, i) => ({ ...r, id: i })));
            setResults(null);
            toast.success(`${parsed.length} fila${parsed.length !== 1 ? 's' : ''} cargada${parsed.length !== 1 ? 's' : ''}`);
        };
        reader.onerror = () => toast.error('Error al leer el archivo');
        reader.readAsText(file, 'utf-8');
        e.target.value = ''; // permite reseleccionar el mismo archivo
    };

    // Editar rol de una fila
    const updateRole = (id, role) =>
        setRows(prev => prev.map(r => r.id === id ? { ...r, role } : r));

    // Quitar una fila
    const removeRow = (id) =>
        setRows(prev => prev.filter(r => r.id !== id));

    // Descargar plantilla de ejemplo
    const downloadTemplate = () => {
        const csv = [
            'email,role',
            'usuario@empresa.com,user',
            'agente@empresa.com,agent',
            'autorizador@empresa.com,authorizer',
        ].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.download = 'plantilla_migracion.csv'; a.click();
        URL.revokeObjectURL(url);
    };

    // Enviar invitaciones
    const handleSend = async () => {
        if (!rows.length) return;
        setSending(true);
        try {
            const { data } = await api.post('/auth/bulk-invite', {
                invitations: rows.map(({ email, role }) => ({ email, role })),
            });
            setResults(data.results);
            setRows([]);
            const sent    = data.results.filter(r => r.status === 'sent').length;
            const skipped = data.results.filter(r => r.status === 'skipped').length;
            const failed  = data.results.filter(r => r.status === 'failed').length;
            if (failed === 0) toast.success(`${sent} enviadas, ${skipped} omitidas`);
            else              toast.warn(`${sent} enviadas · ${skipped} omitidas · ${failed} fallidas`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar invitaciones');
        } finally {
            setSending(false);
        }
    };

    // Badge de estado en resultados
    const statusBadge = (status) => {
        if (status === 'sent')    return <Badge bg="success">Enviada</Badge>;
        if (status === 'skipped') return <Badge bg="warning" text="dark">Omitida</Badge>;
        return                           <Badge bg="danger">Fallida</Badge>;
    };

    const sentCount    = results?.filter(r => r.status === 'sent').length    ?? 0;
    const skippedCount = results?.filter(r => r.status === 'skipped').length ?? 0;
    const failedCount  = results?.filter(r => r.status === 'failed').length  ?? 0;

    return (
        <Container style={{ maxWidth: 860, margin: '2rem auto' }}>
            <h2>Migración de usuarios</h2>
            <p className="text-muted mb-4">
                Importa usuarios desde Zoho Help Desk u otro sistema mediante un CSV
                y envía invitaciones de registro masivas por correo electrónico.
            </p>

            {/* ── Paso 1: Cargar archivo ── */}
            <Card className="mb-4">
                <Card.Header><strong>1. Cargar archivo CSV</strong></Card.Header>
                <Card.Body>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Button
                            variant="primary"
                            onClick={() => fileRef.current.click()}
                            disabled={sending}
                        >
                            Seleccionar archivo CSV
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={downloadTemplate}>
                            Descargar plantilla
                        </Button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            style={{ display: 'none' }}
                            onChange={handleFile}
                        />
                    </div>
                    <Form.Text className="text-muted d-block mt-2">
                        El CSV debe tener una columna <code>email</code> y opcionalmente <code>role</code>{' '}
                        (valores: <code>user</code>, <code>agent</code>, <code>authorizer</code>).
                        Las filas sin rol válido se asignan como <em>Usuario</em>.
                    </Form.Text>
                </Card.Body>
            </Card>

            {/* ── Paso 2: Preview editable ── */}
            {rows.length > 0 && (
                <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <strong>
                            2. Revisar y ajustar&nbsp;
                            <span className="text-muted fw-normal">
                                ({rows.length} {rows.length === 1 ? 'fila' : 'filas'})
                            </span>
                        </strong>
                        <Button
                            variant="success"
                            disabled={sending}
                            onClick={handleSend}
                        >
                            {sending ? (
                                <><Spinner size="sm" animation="border" className="me-2" />Enviando…</>
                            ) : (
                                `Enviar ${rows.length} invitación${rows.length !== 1 ? 'es' : ''}`
                            )}
                        </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            <Table size="sm" hover className="mb-0">
                                <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                                    <tr>
                                        <th style={{ width: 40 }}>#</th>
                                        <th>Email</th>
                                        <th style={{ width: 160 }}>Rol</th>
                                        <th style={{ width: 40 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, idx) => (
                                        <tr key={r.id}>
                                            <td className="text-muted">{idx + 1}</td>
                                            <td>{r.email || <span className="text-danger">— vacío —</span>}</td>
                                            <td>
                                                <Form.Select
                                                    size="sm"
                                                    value={r.role}
                                                    onChange={e => updateRole(r.id, e.target.value)}
                                                >
                                                    <option value="user">Usuario</option>
                                                    <option value="agent">Agente</option>
                                                    <option value="authorizer">Autorizador</option>
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="text-danger p-0"
                                                    title="Quitar fila"
                                                    onClick={() => removeRow(r.id)}
                                                >
                                                    ✕
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* ── Resultados ── */}
            {results && (
                <Card>
                    <Card.Header className="d-flex align-items-center gap-3 flex-wrap">
                        <strong>Resultados</strong>
                        <Badge bg="success">{sentCount} enviadas</Badge>
                        <Badge bg="warning" text="dark">{skippedCount} omitidas</Badge>
                        <Badge bg="danger">{failedCount} fallidas</Badge>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {failedCount > 0 && (
                            <Alert variant="warning" className="m-3 mb-0">
                                Las filas <strong>omitidas</strong> corresponden a correos que ya tienen cuenta.
                                Las filas <strong>fallidas</strong> pueden deberse a un email inválido o un error de correo.
                            </Alert>
                        )}
                        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                            <Table size="sm" hover className="mb-0">
                                <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                                    <tr>
                                        <th>Email</th>
                                        <th>Rol</th>
                                        <th>Estado</th>
                                        <th>Detalle</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((r, i) => (
                                        <tr key={i}>
                                            <td>{r.email}</td>
                                            <td>
                                                <Badge bg={ROLE_COLORS[r.role] || 'secondary'}>
                                                    {ROLE_LABELS[r.role] || r.role}
                                                </Badge>
                                            </td>
                                            <td>{statusBadge(r.status)}</td>
                                            <td className="text-muted small">{r.reason || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                    <Card.Footer className="text-end">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => { setResults(null); fileRef.current.click(); }}
                        >
                            Cargar otro CSV
                        </Button>
                    </Card.Footer>
                </Card>
            )}
        </Container>
    );
}
